import { type JSX, type Ref, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import {
  CustomSelect,
  type RenderSearchItemProps,
} from '@/components/inputs/CustomSelect'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { type Collaborator } from '@/features/jobs/Job'
import {
  RemoveClientSearchItem,
  RemoveClientsAsNonAdminDialog,
} from '@/features/jobs/details/components/RemoveClientsAsNonAdminDialog'
import { type RemoveClientsAsNonAdminDialogViewModel } from './RemoveClientsAsNonAdminDialogViewModel'

export interface RemoveClientsAsNonAdminDialogContainerProps {
  vm: RemoveClientsAsNonAdminDialogViewModel
}

export const RemoveClientsAsNonAdminDialogContainer = observer(
  function RemoveClientsAsNonAdminDialogContainer({
    vm,
  }: RemoveClientsAsNonAdminDialogContainerProps): JSX.Element {
    const itemSource = useMemo(
      () =>
        ItemSource.static<Collaborator>(
          vm.filteredCollaborators,
          (item, text) =>
            item.member.displayName.toLowerCase().includes(text.toLowerCase()),
        ),
      [vm.filteredCollaborators],
    )

    const selectProps = useCustomSelectStateFromItemSource({
      itemSource,
    })

    return (
      <RemoveClientsAsNonAdminDialog
        showing={vm.modal.showing}
        collaboratorError={vm.validation.getError('collaborator')}
        collaboratorSelect={
          <CustomSelect
            {...selectProps}
            multi={false}
            icon="dropdown"
            iconColor="var(--grey600)"
            itemToString={(i) => i.member.displayName}
            placeholder="Select collaborator"
            value={vm.collaborator}
            onChange={vm.setCollaborator}
            renderSearchItem={(props) => (
              <RemoveSearchItemContainer {...props} />
            )}
            testId="remove-job-collaborator-as-non-admin-dropdown"
          />
        }
        isRemovePending={vm.removeClient.pending}
        isRemoveRejected={vm.removeClient.rejected}
        removeError={vm.removeClient.error}
        isDisabled={vm.disabled}
        onClose={vm.modal.close}
        onRemove={vm.removeClient}
        submitButtonTestId="remove-job-collaborator-as-non-admin-submit-button"
      />
    )
  },
)

interface RemoveSearchItemContainerProps extends RenderSearchItemProps<Collaborator> {
  ref?: Ref<HTMLDivElement>
}

const RemoveSearchItemContainer = observer(function RemoveSearchItemContainer({
  item,
  itemToString: _itemToString,
  ref,
  ...itemProps
}: RemoveSearchItemContainerProps) {
  return (
    <RemoveClientSearchItem
      ref={ref}
      {...itemProps}
      testId={
        'remove-job-collaborator-as-non-admin-option-' +
        item.member.user.publicId?.toLowerCase()
      }
    >
      {item.member.displayName}
    </RemoveClientSearchItem>
  )
})
